<template>
  <div class="grid grid-cols-12 gap-4">
    <div class="col-span-12 md:col-span-4 lg:col-span-2 flex">
      <button @click="lodgeComplaintPrompt" type="button" class="btn-primary bg-primary hover:bg-primary-dark text-white rounded-sm px-2 py-1 my-auto">
        Lodge Complaint
      </button>
    </div>
    <div class="col-span-12 pt-3" v-if="complaints.length === 0">
      <div class="bg-gray-100 border border-gray-300 rounded px-4 py-3 text-gray-700">
        <i class="fa fa-info-circle mr-2"></i> {{ name }} has no complaints lodged against {{ gender === null || gender === 'MALE' ? 'him' : 'her'  }}.
      </div>
    </div>
    <div class="col-span-12 pt-3" v-else>
      <table class="min-w-full leading-normal mt-1 text-gray" style="border: solid 1px #dedede;">
        <thead>
        <tr>
          <th class="px-2 py-1">Tracking Number</th>
          <th class="px-2 py-1">Complainant</th>
          <th class="px-2 py-1">Complaint</th>
          <th class="px-2 py-1 text-center">Date</th>
          <th class="px-2 py-1 text-center">Status</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(complaint, idx) in complaints" :key="idx" style="border: solid 1px #dedede;">
          <td class="px-2 py-1">{{complaint.trackingNumber}}</td>
          <td class="px-2 py-1 text-capitalize">{{complaint.complainantName}}</td>
          <td class="px-2 py-1 text-capitalize relative">
            {{complaint.complaint}}
            <span class="relative inline-block">
              <i :id="'complaint-details-'+(idx+1)" @click="togglePopover(idx)" tabindex="0" class="fa fa-info-circle text-blue ml-2 cursor-pointer"></i>
              <div v-if="activePopover === idx" @click.stop class="absolute left-0 top-6 z-50 w-64 bg-white border border-gray-300 rounded-lg shadow-lg p-3">
                <div class="font-bold text-sm mb-2">{{complaint.complaint}}</div>
                <div class="text-sm text-gray-600">{{complaint.description}}</div>
                <button @click="activePopover = null" class="absolute top-1 right-1 text-gray-400 hover:text-gray-600">
                  <i class="fa fa-times"></i>
                </button>
              </div>
            </span>
          </td>
          <td class="px-2 py-1 text-center">{{complaint.status}}</td>
          <td class="px-2 py-1 text-center">{{dateFormat(complaint.dateOfApplication)}}</td>
        </tr>
        </tbody>
      </table>
    </div>

    <AppModal v-model="showLodgeComplaints" centered no-close-on-backdrop hide-header-close hide-footer title="Lodge Complaint">
      <LodgeComplaint :practitioner-number="upn" @close="showLodgeComplaints = false" />
    </AppModal>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import AppModal from "@/components/ui/AppModal.vue";
import { usePractitionersRegistryStore } from "@/stores/practitioners-registry";
import { dateFormat } from "@/commons/composables/utilities";
import LodgeComplaint from "@/domains/home/services/registry/practitioners/LodgeComplaint.vue";

const props = defineProps({
  upn: {
    type: String,
    required: true
  },
  name: {
    type: String,
    default: null
  },
  gender: {
    type: String,
    default: null
  }
});

const store = usePractitionersRegistryStore();

const showLodgeComplaints = ref(false);
const loading = ref(false);
const complaints = ref<any[]>([]);
const pagination = ref({
  totalElements: 0,
  totalPages: 0
});
const query = ref({
  page: 1,
  perPage: 20,
  status: null as string | null,
  practitionerNumber: props.upn
});
const activePopover = ref<number | null>(null);

onMounted(() => {
  loading.value = true;
  store.fetchPractitionerComplaints(query.value);
});

const togglePopover = (idx: number) => {
  activePopover.value = activePopover.value === idx ? null : idx;
};

const fetch = () => {
  loading.value = true;
  store.fetchPractitionerComplaints(query.value);
};

const next = () => {
  if (query.value.page < pagination.value.totalPages) {
    query.value.page++;
    fetch();
  }
};

const previous = () => {
  if (query.value.page > 1) {
    query.value.page--;
    fetch();
  }
};

const lodgeComplaintPrompt = () => {
  showLodgeComplaints.value = true;
}

watch(() => store.practitioner_complaints, (response) => {
  if (response) {
    complaints.value = response.data;
    loading.value = false;
  }
});
</script>
